import React, { useState, useEffect } from "react";
import { object, func } from "prop-types";
import { connect } from "react-redux";
import { WithMember } from "@quintype/components";
import get from "lodash/get";
import ImageUploading from "react-images-uploading";

import Button from "../../atoms/Button";
import TextField from "../../atoms/TextField";
import GenderIcon from "../../atoms/icons/gender";
import ProfileHeadline from "../../atoms/ProfileHeadline";
import MessageView from "../../atoms/MessageView";
import ContributorImage from "../../atoms/ContributorImage";
import Plus from "../../atoms/icons/Plus";

import {
  updateUserInformation,
  signImage,
  memberProfileUpdate,
  uploadS3ToTemp,
  getCountriesList,
} from "../../helper/api";
import { setLoading, setMember } from "../../helper/actions";
import { getMember } from "../../utils/utils";

import "./edit-profile.m.css";

const EditProfile = ({ member, setLoading, navigateToPage, publisherAttributes, setMember }) => {
  const formatDob = (dateString) => {
    const parts = dateString.split("-");
    return parts.length > 0
      ? new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10))
      : null;
  };

  const [error, setError] = useState(null);
  const [name, setName] = useState(member.name || "");
  const [countriesList, setCountriesList] = useState([]);
  const [nameError, setNameError] = useState(null);
  const [metadata, setMetadata] = useState(member.metadata || {});
  const [selectedGender, setSelectedGender] = useState(metadata.gender ? metadata.gender : "male"); // default male
  const [birthday, setBirthday] = useState(
    member.metadata && member.metadata.dob ? formatDob(member.metadata.dob) : null
  );
  const [tempImageObj, setTempImageObj] = useState(null);
  const [blurImage, setBlurImage] = useState(false);
  const [DatePicker, setDatePicker] = useState(null);
  const memberObj = getMember(member);

  const genderSelection = (type) => {
    setSelectedGender(type);
  };

  const setData = (e) => {
    const metadataObj = { ...metadata };
    const fieldName = e.target.name;
    metadataObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && metadataObj[`${fieldName}Error`]) {
      metadataObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setMetadata(metadataObj);
  };

  const nameChange = (e) => {
    // If error has been set then remove it
    if (e.target.value && nameError) {
      setNameError(null);
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setName(e.target.value);
  };

  const dateSelected = (newDate) => setBirthday(newDate);

  const onSubmitClick = (e) => {
    if (name.length === 0) {
      return;
    }
    e.preventDefault();

    const data = { ...metadata };
    data.gender = selectedGender;

    if (birthday) {
      const date = new Date(birthday);
      let dd = date.getDate();
      const mm = date.getMonth() + 1;
      const yyyy = date.getFullYear();

      if (dd < 10) {
        dd = `0${dd}`;
      }

      data.dob = `${dd}-${mm}-${yyyy}`;
    }

    setLoading(true);
    updateUserInformation({ metadata: data })
      .then(() => {
        if (member.name !== name) {
          memberProfileUpdate({ name: name }).then((resp) => {
            tempImageObj
              ? setMember({ ...member, ...{ metadata: data }, ...tempImageObj, ...{ name: resp.name } })
              : setMember({ ...member, ...{ metadata: data }, ...resp });
            resp.name !== member.name && navigateToPage("/profile");
          });
        } else {
          setMember({ ...member, ...{ metadata: data }, ...tempImageObj });
          navigateToPage("/profile");
        }
      })
      .catch((ex) => setError(ex.message || "Please try after some time."))
      .finally(() => setLoading(false));
  };

  const prepareFormData = (keys, formdata, imageList, res) => {
    return keys.map((key) => {
      if (key === "file") {
        return formdata.append(key, imageList[0].file);
      } else {
        return formdata.append(key, res[key]);
      }
    });
  };

  // Profile pic change
  const onProfileChange = (imageList) => {
    setBlurImage(true);
    signImage(imageList[0].file.name, imageList[0].file.type).then((res) => {
      const formdata = new FormData();
      const keys = [
        "key",
        "Content-Type",
        "policy",
        "acl",
        "success_action_status",
        "AWSAccessKeyId",
        "signature",
        "file",
      ];

      prepareFormData(keys, formdata, imageList, res);

      uploadS3ToTemp(res, formdata).then((response) => {
        if (response.status === 201) {
          memberProfileUpdate({ s3Key: res.key })
            .then((resp) => {
              if (!resp) {
                setBlurImage(false);
                return;
              }
              setTempImageObj(resp);
              setBlurImage(false);
            })
            .catch((err) => {
              setBlurImage(false);
              console.log(err);
            });
        }
      });
    });
  };

  const dateLabelClick = async () => {
    const datePickerImport = await import(/* webpackChunkName: "react-date-picker" */ "../../atoms/ReactDatePicker");
    setDatePicker(datePickerImport.default);
  };

  useEffect(() => {
    getCountriesList().then((resp) => setCountriesList(resp));
    dateLabelClick();
  }, []);

  return (
    <WithMember>
      {() => (
        <form onSubmit={(e) => onSubmitClick(e)}>
          <div styleName={`container ${publisherAttributes.lang}-container`}>
            <ProfileHeadline
              title="Edit Profile"
              type="edit-profile"
              arrowClick={() => navigateToPage("/profile")}
              lang={publisherAttributes.lang}
            />
            <ImageUploading onChange={onProfileChange}>
              {({ imageList, onImageUpload, onImageRemoveAll }) => (
                <React.Fragment>
                  <div styleName="profile-pic">
                    <div styleName="with-upload-wrapper">
                      <ContributorImage
                        authorObj={tempImageObj || memberObj}
                        customStyle={true}
                        blurStyle={blurImage}
                        memberProfilePic={true}
                      />
                      <button styleName="upload-image" type="button" onClick={onImageUpload}>
                        <Plus />
                      </button>
                    </div>
                    <span styleName="email-value">{member.email}</span>
                  </div>
                </React.Fragment>
              )}
            </ImageUploading>

            <div styleName="details">
              <span styleName="personal-info">Personal Information</span>

              <div styleName="gender-wrapper">
                <span styleName="gender-label">Gender</span>
                <div styleName="gender">
                  <div
                    styleName={`male-view ${selectedGender === "male" ? "selected-gender" : "unselected-gender"}`}
                    onClick={() => genderSelection("male")}
                  >
                    <GenderIcon type="male" />
                  </div>
                  <div
                    styleName={`female-view ${selectedGender === "female" ? "selected-gender" : "unselected-gender"}`}
                    onClick={() => genderSelection("female")}
                  >
                    <GenderIcon type="female" />
                  </div>
                </div>
              </div>

              <div styleName="column-fields">
                <TextField
                  value={name}
                  error={nameError}
                  label="Full Name"
                  name="name"
                  inputType="type2"
                  onChange={nameChange}
                  required
                />
                <div>
                  <label styleName="country-label">Country / Region</label>
                  <select styleName="countries-list" name="address" value="address" onChange={setData}>
                    <option value={metadata.address}>{metadata.address}</option>
                    {countriesList.length > 0
                      ? countriesList.map((item, index) => (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        ))
                      : null}
                  </select>
                </div>
              </div>

              <div styleName="column-fields">
                <TextField
                  value={metadata["contact-number"]}
                  error={metadata["contact-numberError"]}
                  label="Phone Contact"
                  name="contact-number"
                  inputType="type2"
                  onChange={setData}
                  type="number"
                  styleName="contact-number"
                />

                {DatePicker ? (
                  <div styleName="birthday-view">
                    <label styleName="datepicker-label">Birthday</label>
                    <DatePicker
                      onDateSelect={dateSelected}
                      src="edit-profile"
                      startPlaceholder="Date of Birth"
                      date={birthday}
                      lang={publisherAttributes.lang}
                    />
                  </div>
                ) : null}
              </div>

              <MessageView type="error" text={error} />

              <div styleName="column-fields">
                <Button content="SAVE" buttonType="type4" type="submit" />
                <div />
              </div>
            </div>
          </div>
        </form>
      )}
    </WithMember>
  );
};

EditProfile.propTypes = {
  member: object.isRequired,
  navigateToPage: func,
  setLoading: func,
  publisherAttributes: object,
  setMember: func,
};

const mapStateToProps = (state) => ({
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
});

const mapDispatchToProps = (dispatch) => ({
  setMember: (member) => dispatch(setMember(member)),
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditProfile);
